import React, { useState, useRef, useEffect } from 'react';
import {
  Users, UserCircle, Heart, Briefcase,
  Link, Upload, Check, Loader2, ChevronDown,
  ArrowRight, Calendar, CheckSquare,
} from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useSpace } from '../../contexts/SpaceContext';
import { useGoogleCalendarSync, type GoogleCalendar } from '../../hooks/useGoogleCalendarSync';
import { parseICSContent, fetchICSFromUrl } from '../../utils/icsParser';
import { Logo } from '../ui/Logo';
import { track } from '../../utils/analytics';

/* ─────────────────────────── types ─────────────────────────── */

type UseCase = 'personal' | 'family' | 'team';
type WizardStep = 'welcome' | 'profile' | 'use-case' | 'invite' | 'import' | 'done';

function getStepSequence(useCase: UseCase | null): WizardStep[] {
  const seq: WizardStep[] = ['welcome', 'profile', 'use-case'];
  if (useCase === 'team') seq.push('invite');
  seq.push('import', 'done');
  return seq;
}

interface OnboardingWizardProps {
  onComplete: () => void;
}

/* ─────────────────────────── shared button ─────────────────── */

function PrimaryBtn({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all
        hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed
        disabled:hover:scale-100 flex items-center justify-center gap-2 ${className}`}
      style={{ backgroundColor: 'var(--accent-primary)' }}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-primary)'; }}
    >
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full py-1.5 text-sm text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] transition-colors"
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── Step: Welcome ─────────────────── */

function StepWelcome({ onNext }: { onNext: () => void }) {
  return (
    <div>
      <h1 className="text-[22px] font-semibold text-[var(--content-primary)] mb-1.5 tracking-tight">
        Welcome to Mozayc
      </h1>
      <p className="text-sm text-[var(--content-secondary)] mb-8">
        Your visual calendar for the whole picture.
      </p>

      <div className="space-y-4 mb-8">
        {[
          {
            Icon: Calendar,
            title: 'See your year at a glance',
            desc: 'All 12 months on one screen — spot patterns and plan ahead.',
            cls: 'wiz-b1',
          },
          {
            Icon: CheckSquare,
            title: 'Plan events and tasks together',
            desc: 'Built-in kanban boards and task lists alongside your calendar.',
            cls: 'wiz-b2',
          },
          {
            Icon: Users,
            title: 'Collaborate in shared spaces',
            desc: 'Invite your team or family to plan and stay in sync.',
            cls: 'wiz-b3',
          },
        ].map(({ Icon, title, desc, cls }) => (
          <div key={title} className={`flex items-start gap-3 ${cls}`}>
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: 'var(--accent-subtle)' }}
            >
              <Icon className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--content-primary)]">{title}</p>
              <p className="text-xs text-[var(--content-secondary)] mt-0.5 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <PrimaryBtn onClick={onNext}>Get started</PrimaryBtn>
    </div>
  );
}

/* ─────────────────────────── Step: Profile ─────────────────── */

interface StepProfileProps {
  displayName: string;
  setDisplayName: (v: string) => void;
  avatarUrl: string;
  initials: string;
  uploadingAvatar: boolean;
  loading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onContinue: () => void;
}

function StepProfile({
  displayName,
  setDisplayName,
  avatarUrl,
  initials,
  uploadingAvatar,
  loading,
  fileInputRef,
  onContinue,
}: StepProfileProps) {
  return (
    <div>
      <h2 className="text-[22px] font-semibold text-[var(--content-primary)] mb-1.5 tracking-tight">
        What should we call you?
      </h2>
      <p className="text-sm text-[var(--content-secondary)] mb-7">
        Set a name and photo for your profile.
      </p>

      {/* Avatar */}
      <div className="flex justify-center mb-6">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingAvatar}
          className="relative w-[76px] h-[76px] rounded-full group focus:outline-none"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <div
              className="w-full h-full rounded-full flex items-center justify-center text-lg font-semibold text-white select-none"
              style={{ backgroundColor: 'var(--accent-primary)' }}
            >
              {uploadingAvatar
                ? <Loader2 className="w-5 h-5 animate-spin" />
                : initials}
            </div>
          )}
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Upload className="w-4 h-4 text-white" />
          </div>
        </button>
      </div>
      <p className="text-center text-xs text-[var(--content-tertiary)] -mt-4 mb-6">
        Click to upload · max 2 MB
      </p>

      {/* Name */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1.5">
          Display name
        </label>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && displayName.trim()) onContinue(); }}
          autoFocus
          placeholder="Your name"
          className="w-full px-3 py-2 rounded-lg text-sm bg-[var(--input-bg)] border border-[var(--input-border)]
            focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]
            text-[var(--content-primary)] placeholder-[var(--content-tertiary)]"
        />
      </div>

      <PrimaryBtn onClick={onContinue} disabled={!displayName.trim() || loading}>
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Continue'}
      </PrimaryBtn>
    </div>
  );
}

/* ─────────────────────────── Step: Use-case ─────────────────── */

const USE_CASES: { id: UseCase; Icon: React.FC<{ className?: string }>; label: string; desc: string }[] = [
  { id: 'personal', Icon: UserCircle, label: 'For myself',    desc: 'Personal planning, goals, and events' },
  { id: 'family',   Icon: Heart,       label: 'With family',  desc: 'Shared family calendar and activities' },
  { id: 'team',     Icon: Briefcase,   label: 'With my team', desc: 'Work projects, sprints, and deadlines' },
];

function StepUseCase({
  selected,
  onSelect,
  onContinue,
}: {
  selected: UseCase | null;
  onSelect: (v: UseCase) => void;
  onContinue: () => void;
}) {
  return (
    <div>
      <h2 className="text-[22px] font-semibold text-[var(--content-primary)] mb-1.5 tracking-tight">
        How will you use Mozayc?
      </h2>
      <p className="text-sm text-[var(--content-secondary)] mb-7">
        We'll personalize your experience.
      </p>

      <div className="space-y-2.5 mb-7">
        {USE_CASES.map(({ id, Icon, label, desc }) => {
          const active = selected === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all"
              style={{
                backgroundColor: active ? 'var(--accent-subtle)' : 'var(--surface-secondary)',
                borderColor: active ? 'var(--accent-primary)' : 'var(--border-primary)',
                boxShadow: active ? '0 0 0 1px var(--accent-primary)' : 'none',
              }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                style={{ backgroundColor: active ? 'var(--accent-primary)' : 'var(--surface-tertiary)' }}
              >
                <Icon
                  className="w-[18px] h-[18px]"
                  // @ts-ignore — lucide className + style mix
                  style={{ color: active ? 'white' : 'var(--content-secondary)' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--content-primary)]">{label}</p>
                <p className="text-xs text-[var(--content-secondary)] mt-0.5">{desc}</p>
              </div>
              {active && (
                <Check
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: 'var(--accent-primary)' }}
                />
              )}
            </button>
          );
        })}
      </div>

      <PrimaryBtn onClick={onContinue} disabled={!selected}>Continue</PrimaryBtn>
    </div>
  );
}

/* ─────────────────────────── Step: Invite ─────────────────── */

function StepInvite({
  emails,
  setEmails,
  loading,
  results,
  onContinue,
  onSkip,
}: {
  emails: string[];
  setEmails: (v: string[]) => void;
  loading: boolean;
  results: Record<number, 'success' | 'error'>;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const filled = emails.filter(e => e.trim()).length;

  return (
    <div>
      <h2 className="text-[22px] font-semibold text-[var(--content-primary)] mb-1.5 tracking-tight">
        Invite your team
      </h2>
      <p className="text-sm text-[var(--content-secondary)] mb-7">
        Add teammates to start collaborating. You can always invite more from Settings.
      </p>

      <div className="space-y-2.5 mb-7">
        {emails.map((email, i) => (
          <div key={i} className="relative">
            <input
              type="email"
              value={email}
              onChange={e => {
                const next = [...emails];
                next[i] = e.target.value;
                setEmails(next);
              }}
              placeholder={i === 0 ? 'colleague@company.com' : 'another@company.com'}
              className={`w-full px-3 py-2 rounded-lg text-sm bg-[var(--input-bg)] border
                focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]
                text-[var(--content-primary)] placeholder-[var(--content-tertiary)]
                ${results[i] === 'success' ? 'border-[var(--success)]' :
                  results[i] === 'error'   ? 'border-[var(--error)]'   :
                  'border-[var(--input-border)]'}`}
            />
            {results[i] === 'success' && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--success)' }} />
            )}
          </div>
        ))}
      </div>

      <PrimaryBtn onClick={onContinue} disabled={loading} className="mb-2">
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending invites…</>
          : filled > 0
            ? `Send ${filled} invite${filled > 1 ? 's' : ''} & continue`
            : 'Continue'}
      </PrimaryBtn>
      <GhostBtn onClick={onSkip}>Skip for now</GhostBtn>
    </div>
  );
}

/* ─────────────────────────── Step: Import ─────────────────── */

const GoogleIcon = () => (
  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

function ImportTile({
  icon,
  title,
  subtitle,
  done,
  expandable,
  expanded,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  done?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-primary)' }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!expandable && !onToggle}
        className="w-full flex items-center gap-3 p-3.5 text-left transition-colors hover:bg-[var(--surface-tertiary)] disabled:cursor-default disabled:hover:bg-transparent"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: done ? 'var(--accent-subtle)' : 'var(--surface-tertiary)' }}
        >
          {done
            ? <Check className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            : icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--content-primary)]">{title}</p>
          <p className="text-xs text-[var(--content-secondary)] mt-0.5">{done ? '✓ Added' : subtitle}</p>
        </div>
        {expandable && (
          <ChevronDown
            className="w-4 h-4 flex-shrink-0 text-[var(--content-tertiary)] transition-transform duration-150"
            style={{ transform: expanded ? 'rotate(180deg)' : '' }}
          />
        )}
      </button>
      {expanded && children && (
        <div className="px-3.5 pb-3.5 pt-0">{children}</div>
      )}
    </div>
  );
}

function StepImport({
  gcalConnected,
  gcalSyncing,
  gcalSynced,
  gcalPicking,
  gcalCalendars,
  gcalSelected,
  onGcalConnect,
  onGcalToggle,
  onGcalImport,
  webcalUrl,
  setWebcalUrl,
  webcalLoading,
  webcalAdded,
  webcalError,
  onWebcalAdd,
  icsFileRef,
  icsLoading,
  icsAdded,
  activePanel,
  setActivePanel,
  onContinue,
}: {
  gcalConnected: boolean;
  gcalSyncing: boolean;
  gcalSynced: boolean;
  gcalPicking: boolean;
  gcalCalendars: GoogleCalendar[];
  gcalSelected: Set<string>;
  onGcalConnect: () => void;
  onGcalToggle: (id: string) => void;
  onGcalImport: () => void;
  webcalUrl: string;
  setWebcalUrl: (v: string) => void;
  webcalLoading: boolean;
  webcalAdded: boolean;
  webcalError: string;
  onWebcalAdd: () => void;
  icsFileRef: React.RefObject<HTMLInputElement | null>;
  icsLoading: boolean;
  icsAdded: boolean;
  activePanel: 'url' | 'ics' | null;
  setActivePanel: (v: 'url' | 'ics' | null) => void;
  onContinue: () => void;
}) {
  return (
    <div>
      <h2 className="text-[22px] font-semibold text-[var(--content-primary)] mb-1.5 tracking-tight">
        Connect your calendar
      </h2>
      <p className="text-sm text-[var(--content-secondary)] mb-7">
        Import existing events — or skip and start fresh.
      </p>

      <div className="space-y-2.5 mb-7">
        {/* Google Calendar */}
        <ImportTile
          icon={<GoogleIcon />}
          title="Google Calendar"
          subtitle="Sync one or more Google calendars"
          done={gcalSynced}
        >
          {!gcalSynced && (
            <div className="mt-1">
              {!gcalConnected ? (
                /* Not yet authorized — show connect button */
                <button
                  type="button"
                  onClick={onGcalConnect}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5"
                  style={{ backgroundColor: 'var(--accent-primary)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-hover)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-primary)'; }}
                >
                  Connect Google Calendar
                </button>
              ) : gcalPicking ? (
                /* Authorized — calendar picker */
                <div className="space-y-1.5">
                  {gcalCalendars.length === 0 ? (
                    <p className="text-xs text-[var(--content-tertiary)] flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading calendars…
                    </p>
                  ) : (
                    <>
                      {gcalCalendars.map(cal => (
                        <label key={cal.id} className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={gcalSelected.has(cal.id)}
                            onChange={() => onGcalToggle(cal.id)}
                            className="rounded border-[var(--border-primary)] accent-[var(--accent-primary)]"
                          />
                          <span className="text-xs text-[var(--content-primary)]">
                            {cal.summary}
                            {cal.primary && (
                              <span className="ml-1 text-[var(--content-tertiary)]">(primary)</span>
                            )}
                          </span>
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={onGcalImport}
                        disabled={gcalSelected.size === 0 || gcalSyncing}
                        className="mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: 'var(--accent-primary)' }}
                      >
                        {gcalSyncing && <Loader2 className="w-3 h-3 animate-spin" />}
                        {gcalSyncing ? 'Importing…' : 'Import selected'}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                /* Connected but picker not yet open */
                <button
                  type="button"
                  onClick={onGcalConnect}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5"
                  style={{ backgroundColor: 'var(--accent-primary)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-hover)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-primary)'; }}
                >
                  Select calendars
                </button>
              )}
            </div>
          )}
        </ImportTile>

        {/* WebCal URL */}
        <ImportTile
          icon={<Link className="w-4 h-4" style={{ color: 'var(--content-secondary)' }} />}
          title="Subscribe to calendar URL"
          subtitle="Webcal, iCal, or iCloud feed"
          done={webcalAdded}
          expandable
          expanded={activePanel === 'url' && !webcalAdded}
          onToggle={() => setActivePanel(activePanel === 'url' ? null : 'url')}
        >
          <div className="space-y-2 mt-1">
            <input
              type="url"
              value={webcalUrl}
              onChange={e => setWebcalUrl(e.target.value)}
              placeholder="webcal:// or https://…"
              autoFocus
              className="w-full px-3 py-2 rounded-lg text-sm bg-[var(--input-bg)] border border-[var(--input-border)]
                focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]
                text-[var(--content-primary)] placeholder-[var(--content-tertiary)]"
            />
            {webcalError && (
              <p className="text-xs text-[var(--error)]">{webcalError}</p>
            )}
            <button
              type="button"
              onClick={onWebcalAdd}
              disabled={!webcalUrl.trim() || webcalLoading}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: 'var(--accent-primary)' }}
            >
              {webcalLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              {webcalLoading ? 'Adding…' : 'Add calendar'}
            </button>
          </div>
        </ImportTile>

        {/* ICS File */}
        <ImportTile
          icon={
            icsLoading
              ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--content-secondary)' }} />
              : <Upload className="w-4 h-4" style={{ color: 'var(--content-secondary)' }} />
          }
          title="Upload .ics file"
          subtitle="Apple Calendar, Outlook, and more"
          done={icsAdded}
          onToggle={() => icsFileRef.current?.click()}
        />
      </div>

      <PrimaryBtn onClick={onContinue}>
        Continue <ArrowRight className="w-4 h-4" />
      </PrimaryBtn>
    </div>
  );
}

/* ─────────────────────────── Step: Done ─────────────────── */

function StepDone({
  firstName,
  avatarUrl,
  initials,
  onComplete,
}: {
  firstName: string;
  avatarUrl: string;
  initials: string;
  onComplete: () => void;
}) {
  return (
    <div className="text-center">
      {/* Avatar */}
      <div className="flex justify-center mb-5">
        <div
          className="w-[72px] h-[72px] rounded-full overflow-hidden flex items-center justify-center text-xl font-semibold text-white"
          style={{ backgroundColor: avatarUrl ? 'transparent' : 'var(--accent-primary)' }}
        >
          {avatarUrl
            ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
            : initials}
        </div>
      </div>

      <h2 className="text-[22px] font-semibold text-[var(--content-primary)] mb-2 tracking-tight">
        You're all set, {firstName}!
      </h2>
      <p className="text-sm text-[var(--content-secondary)] mb-8 leading-relaxed max-w-[280px] mx-auto">
        Your calendar is ready. Start by adding an event, or explore what Mozayc can do.
      </p>

      <PrimaryBtn onClick={onComplete}>
        Open Mozayc <ArrowRight className="w-4 h-4" />
      </PrimaryBtn>
    </div>
  );
}

/* ─────────────────────────── Main wizard ─────────────────── */

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { user, connectGoogleCalendar } = useAuth();
  const { inviteMember } = useSpace();
  const gcal = useGoogleCalendarSync();

  /* step navigation */
  const [step, setStep] = useState<WizardStep>(() => {
    // Resume at import step if returning from Google Calendar OAuth redirect
    const resume = localStorage.getItem('mozayc-onboarding-resume-step');
    if (resume === 'import') {
      localStorage.removeItem('mozayc-onboarding-resume-step');
      return 'import';
    }
    return 'welcome';
  });
  const [animKey, setAnimKey] = useState(0);
  const [useCase, setUseCase] = useState<UseCase | null>(null);

  const stepSeq = getStepSequence(useCase);
  const currentIdx = stepSeq.indexOf(step);

  const goNext = () => {
    const next = stepSeq[currentIdx + 1];
    if (next) { setStep(next); setAnimKey(k => k + 1); track('onboarding_step', { step: next }); }
  };

  const goPrev = () => {
    const prev = stepSeq[currentIdx - 1];
    if (prev) { setStep(prev); setAnimKey(k => k + 1); }
  };

  /* ── profile ── */
  const [displayName, setDisplayName] = useState(
    () => user?.user_metadata?.full_name
      || user?.user_metadata?.display_name
      || user?.email?.split('@')[0]
      || '',
  );
  const [avatarUrl, setAvatarUrl] = useState<string>(user?.user_metadata?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 2 * 1024 * 1024) return; // 2 MB guard
    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${user.id}/avatar.${ext}`;
      const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      const url = `${publicUrl}?t=${Date.now()}`;
      setAvatarUrl(url);
      await supabase.auth.updateUser({ data: { avatar_url: url } });
    } catch (err) {
      console.error('Avatar upload failed:', err);
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleProfileContinue = async () => {
    const name = displayName.trim();
    if (!name) return;
    setProfileLoading(true);
    await supabase.auth.updateUser({ data: { display_name: name } });
    setProfileLoading(false);
    goNext();
  };

  /* ── use-case ── */
  const handleUseCaseContinue = async () => {
    if (!useCase) return;
    await supabase.auth.updateUser({ data: { onboarding_use_case: useCase } });
    goNext();
  };

  /* ── invite ── */
  const [inviteEmails, setInviteEmails] = useState(['', '', '']);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResults, setInviteResults] = useState<Record<number, 'success' | 'error'>>({});

  const handleInviteContinue = async () => {
    const active = inviteEmails.filter(e => e.trim());
    if (!active.length) { goNext(); return; }
    setInviteLoading(true);
    const res: Record<number, 'success' | 'error'> = {};
    await Promise.all(
      inviteEmails.map(async (email, i) => {
        if (!email.trim()) return;
        const r = await inviteMember(email.trim(), 'editor');
        res[i] = r.success ? 'success' : 'error';
      }),
    );
    setInviteResults(res);
    setInviteLoading(false);
    goNext();
  };

  /* ── import ── */
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalSynced, setGcalSynced] = useState(false);
  const [gcalPicking, setGcalPicking] = useState(false);
  const [gcalCalendars, setGcalCalendars] = useState<GoogleCalendar[]>([]);
  const [gcalSelected, setGcalSelected] = useState<Set<string>>(new Set());

  // If returning from OAuth (gcal.connected just became true), open picker
  useEffect(() => {
    if (gcal.connected && step === 'import' && !gcalPicking && !gcalSynced) {
      setGcalPicking(true);
      gcal.listCalendars().then(cals => {
        setGcalCalendars(cals);
        // Pre-select primary calendar
        const primary = cals.find(c => c.primary);
        if (primary) setGcalSelected(new Set([primary.id]));
      });
    }
  }, [gcal.connected, step]);

  const handleGcalConnect = async () => {
    if (!gcal.connected) {
      // Not yet authorized — start OAuth flow; will redirect away and resume here
      localStorage.setItem('mozayc-onboarding-resume-step', 'import');
      await connectGoogleCalendar('onboarding');
      return;
    }
    // Already connected — open picker
    setGcalPicking(true);
    if (gcalCalendars.length === 0) {
      const cals = await gcal.listCalendars();
      setGcalCalendars(cals);
      const primary = cals.find(c => c.primary);
      if (primary) setGcalSelected(new Set([primary.id]));
    }
  };

  const handleGcalToggle = (id: string) => {
    setGcalSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleGcalImport = async () => {
    const selectedCals = gcalCalendars.filter(c => gcalSelected.has(c.id));
    if (selectedCals.length === 0) return;
    setGcalSyncing(true);
    try {
      await gcal.connectCalendars(selectedCals);
      await gcal.pullChanges();
      setGcalSynced(true);
      setGcalPicking(false);
    } catch {
      // non-fatal
    } finally {
      setGcalSyncing(false);
    }
  };

  const [webcalUrl, setWebcalUrl] = useState('');
  const [webcalLoading, setWebcalLoading] = useState(false);
  const [webcalAdded, setWebcalAdded] = useState(false);
  const [webcalError, setWebcalError] = useState('');

  const [icsLoading, setIcsLoading] = useState(false);
  const [icsAdded, setIcsAdded] = useState(false);
  const icsFileRef = useRef<HTMLInputElement>(null);

  const [activePanel, setActivePanel] = useState<'url' | 'ics' | null>(null);

  const handleWebcalAdd = async () => {
    if (!webcalUrl.trim() || !user) return;
    setWebcalLoading(true);
    setWebcalError('');
    try {
      await fetchICSFromUrl(webcalUrl.trim()); // validates + fetches via edge fn
      const { error } = await supabase.from('imported_calendars').insert({
        user_id: user.id,
        name: 'Imported Calendar',
        url: webcalUrl.trim(),
        color: 'bg-blue-500',
        enabled: true,
      });
      if (error) throw error;
      setWebcalAdded(true);
      setActivePanel(null);
    } catch {
      setWebcalError('Could not fetch calendar. Check the URL and try again.');
    } finally {
      setWebcalLoading(false);
    }
  };

  const handleIcsImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setIcsLoading(true);
    try {
      const content = await file.text();
      const { data: cal, error: calErr } = await supabase
        .from('imported_calendars')
        .insert({
          user_id: user.id,
          name: file.name.replace(/\.ics$/i, ''),
          url: 'file://imported',
          color: 'bg-indigo-500',
          enabled: true,
          last_synced_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (calErr) throw calErr;

      const parsed = parseICSContent(content);
      if (parsed.length > 0) {
        const seen = new Map<string, typeof parsed[0]>();
        for (const ev of parsed) seen.set(ev.uid, ev);
        await supabase.from('imported_events').insert(
          Array.from(seen.values()).map(ev => ({
            user_id: user.id,
            calendar_id: cal.id,
            external_uid: ev.uid,
            title: ev.title,
            description: ev.description,
            dates: ev.dates,
            all_day: ev.allDay,
            start_time: ev.startTime,
            end_time: ev.endTime,
            location: ev.location,
          })),
        );
      }
      setIcsAdded(true);
    } catch (err) {
      console.error('ICS import failed:', err);
    } finally {
      setIcsLoading(false);
      e.target.value = '';
    }
  };

  /* ── complete ── */
  const handleComplete = async () => {
    track('onboarding_completed');
    await supabase.auth.updateUser({ data: { onboarding_completed: true } });
    onComplete();
  };

  /* ── derived ── */
  const firstName = displayName.trim().split(' ')[0] || 'there';
  const initials = displayName.trim().split(' ').map((p: string) => p[0]).slice(0, 2).join('').toUpperCase() || '?';

  /* ─────────── render ─────────── */
  return (
    <>
      <style>{`
        @keyframes wiz-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wiz-bullet {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .wiz-step  { animation: wiz-in 0.22s cubic-bezier(0.16,1,0.3,1) both; }
        .wiz-b1    { animation: wiz-bullet 0.32s cubic-bezier(0.16,1,0.3,1) 0.08s both; }
        .wiz-b2    { animation: wiz-bullet 0.32s cubic-bezier(0.16,1,0.3,1) 0.18s both; }
        .wiz-b3    { animation: wiz-bullet 0.32s cubic-bezier(0.16,1,0.3,1) 0.28s both; }
      `}</style>

      <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      >
        <div
          className="w-full max-w-[420px] flex flex-col bg-[var(--surface-primary)] rounded-2xl border border-[var(--border-primary)]"
          style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.18)', maxHeight: '92vh', overflowY: 'auto' }}
        >
          {/* Logo */}
          <div className="flex justify-center pt-8 pb-1 px-8">
            <Logo size="md" />
          </div>

          {/* Step content */}
          <div key={animKey} className="wiz-step flex-1 px-8 py-6">
            {step === 'welcome'  && <StepWelcome onNext={goNext} />}
            {step === 'profile'  && (
              <StepProfile
                displayName={displayName}
                setDisplayName={setDisplayName}
                avatarUrl={avatarUrl}
                initials={initials}
                uploadingAvatar={uploadingAvatar}
                loading={profileLoading}
                fileInputRef={avatarFileRef}
                onContinue={handleProfileContinue}
              />
            )}
            {step === 'use-case' && (
              <StepUseCase
                selected={useCase}
                onSelect={setUseCase}
                onContinue={handleUseCaseContinue}
              />
            )}
            {step === 'invite'   && (
              <StepInvite
                emails={inviteEmails}
                setEmails={setInviteEmails}
                loading={inviteLoading}
                results={inviteResults}
                onContinue={handleInviteContinue}
                onSkip={goNext}
              />
            )}
            {step === 'import'   && (
              <StepImport
                gcalConnected={gcal.connected}
                gcalSyncing={gcalSyncing}
                gcalSynced={gcalSynced}
                gcalPicking={gcalPicking}
                gcalCalendars={gcalCalendars}
                gcalSelected={gcalSelected}
                onGcalConnect={handleGcalConnect}
                onGcalToggle={handleGcalToggle}
                onGcalImport={handleGcalImport}
                webcalUrl={webcalUrl}
                setWebcalUrl={setWebcalUrl}
                webcalLoading={webcalLoading}
                webcalAdded={webcalAdded}
                webcalError={webcalError}
                onWebcalAdd={handleWebcalAdd}
                icsFileRef={icsFileRef}
                icsLoading={icsLoading}
                icsAdded={icsAdded}
                activePanel={activePanel}
                setActivePanel={setActivePanel}
                onContinue={goNext}
              />
            )}
            {step === 'done'     && (
              <StepDone
                firstName={firstName}
                avatarUrl={avatarUrl}
                initials={initials}
                onComplete={handleComplete}
              />
            )}
          </div>

          {/* Progress dots + optional back arrow */}
          <div className="flex items-center justify-center gap-1.5 pb-7 relative">
            {/* Back arrow — visible on all steps except welcome and done */}
            {currentIdx > 0 && step !== 'done' && (
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-6 flex items-center justify-center w-7 h-7 rounded-full transition-colors"
                style={{ color: 'var(--content-tertiary)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--content-secondary)'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--surface-tertiary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--content-tertiary)'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                aria-label="Go back"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            {stepSeq.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => { if (i < currentIdx && step !== 'done') { setStep(s); setAnimKey(k => k + 1); } }}
                className="rounded-[3px] transition-all duration-200"
                style={{
                  width: s === step ? '18px' : '7px',
                  height: '5px',
                  backgroundColor: i <= currentIdx
                    ? 'var(--accent-primary)'
                    : 'var(--surface-tertiary)',
                  border: i <= currentIdx ? 'none' : '1px solid var(--border-secondary)',
                  cursor: i < currentIdx && step !== 'done' ? 'pointer' : 'default',
                  opacity: i < currentIdx && step !== 'done' ? 0.7 : 1,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={avatarFileRef}
        type="file"
        accept="image/*"
        onChange={handleAvatarUpload}
        className="hidden"
      />
      <input
        ref={icsFileRef}
        type="file"
        accept=".ics"
        onChange={handleIcsImport}
        className="hidden"
      />
    </>
  );
}
